/* Odds-page taxonomy: which leagues the selector lists, which markets each
   sport carries, and how a market splits into a base market plus a period.
   
   Shared because ODDS1.jsx and ODDS1Mobile.jsx held byte-identical copies of
   all of it, so every change had to be made twice and the two drifted. Neither
   page owns any of this — it is the same question asked on two screen widths. */
import { SPORT_DISPLAY_NAMES, SPORT_HIERARCHY } from '../config/sports'
import { MARKET_TYPE_FILTER_MAPPING, MARKET_TYPE_HIERARCHY } from '../config/markets'

// Markets available per sport group. All leagues within a group share the same list
// (e.g. WNBA uses NBA's, all eight soccer leagues use Soccer's). SPORT_AVAILABLE_MARKETS
// is derived below by expanding each group across its leagues from SPORT_HIERARCHY.
export const SPORT_GROUP_MARKETS = {
  'Aussie Rules': [
    'Winning Margin 1-39 / 40+', 'Winning Margin 1-24 / 25+',
    'Head To Head', 'Match Handicap (Asian)', 'Total Points', 'Total Goals',
    'Head To Head - 1st Half', 'Match Handicap - 1st Half (Asian)', 'Total Points - 1st Half',
    'Team Total Points', 'Team Total Points - 1st Half',
    'Player Disposals', 'Player Fantasy Points', 'Player Goals',
    'Player Marks', 'Player Tackles', 'Player Kicks', 'Player Handballs', 'Player Clearances',
  ],
  'Rugby League': [
    'Winning Margin 1-12 / 13+',
    'Head To Head', 'Match Handicap (Asian)', 'Total Points', 'Total Tries',
    'Head To Head - 1st Half', 'Match Handicap - 1st Half (Asian)',
    'Total Points - 1st Half', 'Total Tries - 1st Half',
    'Team Total Points', 'Team Total Tries',
    'Team Total Points - 1st Half', 'Team Total Tries - 1st Half',
    'Player Tries', 'Player Points', 'First Tryscorer', 'First or Second Tryscorer', 'First, Second or Third Tryscorer',
  ],
  'Basketball': [
    'Head To Head', 'Match Handicap (Asian)', 'Total Points',
    'Head To Head - 1st Half', 'Match Handicap - 1st Half (Asian)', 'Total Points - 1st Half',
    'Team Total Points', 'Team Total Points - 1st Half',
    'Player Points', 'Player Rebounds', 'Player Assists', 'Player Three Pointers Made',
    'Player Steals', 'Player Blocks', 'Points + Rebounds + Assists',
    'Points + Rebounds', 'Points + Assists', 'Rebounds + Assists',
  ],
  'American Football': [
    'Head To Head', 'Match Handicap (Asian)', 'Total Points', 'Total Touchdowns',
    'Head To Head - 1st Half', 'Match Handicap - 1st Half (Asian)', 'Total Points - 1st Half',
    'Team Total Points', 'Team Total Points - 1st Half',
    'Passing Yards', 'Passing Touchdowns', 'Longest Pass', 'Passing Interceptions',
    'Passing + Rushing Yards', 'Passing Completions', 'Passing Attempts',
    'Rushing Yards', 'Rushing Attempts', 'Longest Rush', 'Rushing + Receiving Yards',
    'Receiving Yards', 'Receptions', 'Longest Reception',
    'Tackles + Assists', 'Sacks',
    'Kicking Points', 'Field Goals Made',
    'Touchdown Scorer', 'First Touchdown Scorer',
  ],
  'Baseball': [
    'Head To Head', 'Match Handicap (Asian)', 'Total Runs',
    'Head To Head - 1st 5 Innings', 'Match Handicap - 1st 5 Innings (Asian)',
    'Total Runs - 1st 5 Innings', 'Total Runs - 1st Inning',
    'Team Total Runs', 'Team Total Runs - 1st 5 Innings', 'Team Total Runs - 1st Inning',
    'Batter Home Runs', 'Batter Hits', 'Batter RBIs', 'Batter Runs',
    'Batter Singles', 'Batter Total Bases', 'Batter Walks', 'Batter Strikeouts',
    'Batter Hits + Runs + RBIs', 'Pitcher Strikeouts', 'Pitcher Earned Runs',
    'Pitcher Hits Allowed', 'Pitcher Walks Allowed', 'Pitcher Outs Recorded',
  ],
  'Hockey': [
    'Head To Head', 'Match Handicap (Asian)', 'Total Goals',
    'Head To Head - 1st Period', 'Head To Head - 2nd Period', 'Head To Head - 3rd Period',
    'Total Goals - 1st Period', 'Total Goals - 2nd Period', 'Total Goals - 3rd Period',
    'Match Handicap - 1st Period (Asian)', 'Match Handicap - 2nd Period (Asian)', 'Match Handicap - 3rd Period (Asian)',
    'Team Total Goals', 'Team Total Goals - 1st Period', 'Team Total Goals - 2nd Period', 'Team Total Goals - 3rd Period',
    'Player Goals', 'Player Points', 'Player Shots on Goal', 'Player Assists',
  ],
  'Soccer': [
    'Head To Head - 3 Way', 'Draw No Bet', 'Both Teams To Score', 'Correct Score',
    'Match Handicap (Asian)', 'Match Handicap - 3 Way',
    'Total Goals', 'Total Corners', 'Total Cards', 'Total Shots', 'Total Shots on Target',
    // 1st half
    'Head To Head - 3 Way 1st Half', 'Draw No Bet - 1st Half', 'Both Teams To Score - 1st Half', 'Correct Score - 1st Half',
    'Match Handicap - 1st Half (Asian)', 'Match Handicap - 3 Way 1st Half',
    'Total Goals - 1st Half', 'Total Corners - 1st Half', 'Total Cards - 1st Half',
    'Total Shots - 1st Half', 'Total Shots on Target - 1st Half',
    // 2nd half
    'Head To Head - 3 Way 2nd Half', 'Draw No Bet - 2nd Half', 'Both Teams To Score - 2nd Half', 'Correct Score - 2nd Half',
    'Match Handicap - 2nd Half (Asian)', 'Match Handicap - 3 Way 2nd Half',
    'Total Goals - 2nd Half', 'Total Corners - 2nd Half', 'Total Cards - 2nd Half',
    'Total Shots - 2nd Half', 'Total Shots on Target - 2nd Half',
    // team totals
    'Team Total Goals', 'Team Total Corners', 'Team Total Cards',
    'Team Total Shots', 'Team Total Shots on Target',
    'Team Total Goals - 1st Half', 'Team Total Corners - 1st Half', 'Team Total Cards - 1st Half',
    'Team Total Shots - 1st Half', 'Team Total Shots on Target - 1st Half',
    'Team Total Goals - 2nd Half', 'Team Total Corners - 2nd Half', 'Team Total Cards - 2nd Half',
    'Team Total Shots - 2nd Half', 'Team Total Shots on Target - 2nd Half',
    // player props
    'First Goalscorer', 'Player Goals', 'Player Shots', 'Player Shots on Target',
    'Player Assists', 'Player Tackles (Soccer)', 'Player Fouls Committed',
    'Player Cards', 'Player Red Cards', 'Goalkeeper Saves',
  ],
  'Tennis': [
    'Head To Head', 'Head To Head - 1st Set', 'Head To Head - 2nd Set',
    'Correct Score (Sets)',
    'Match Handicap - Games (Asian)', 'Match Handicap - Sets (Asian)', 'Match Handicap - Games 1st Set (Asian)', 'Match Handicap - Games 2nd Set (Asian)',
    'Total Games', 'Total Sets', 'Total Aces', 'Total Games - 1st Set', 'Total Games - 2nd Set',
    'Player Games Won', 'Player Total Aces', 'Player Sets Won',
  ],
  // UFC. Fighters are home/away (alphabetical), so the props below name one of
  // the two competitors - see PLAYER_VS_PLAYER_GROUPS.
  'MMA': [
    'Head To Head', 'Fight Goes The Distance', 'How Fight Ends',
    'Method of Victory', 'Round Betting',
    'Total Rounds', 'Fight To Start Round',
    'Fighter Significant Strikes', 'Fighter Total Strikes', 'Fighter Takedowns',
  ],
}

// ─── Virtual leagues (odds page only) ────────────────────────────────────────
//
// The API has NO "ATP" id: the 14 main-tour tournaments are 14 separate ids
// backed by 14 separate collections. At most two run at once, so listing all 14
// is mostly dead rows. This presents ONE league and fans out behind it.
//
// This is a DISPLAY grouping and lives here, not in config/sports.js. The real
// ids stay exactly as they are — tracked bets, saved filters and settlement all
// key on ATP_WIMBLEDON and friends, and must keep doing so.
//
// ATP_CHALLENGER is deliberately NOT a virtual league: it is already one real id
// that returns several tournaments in a single response, so it needs no fan-out.
// Both end up in the same shape downstream — several tournaments in one view,
// each game carrying a `tournament` — which is what the Tournaments filter reads.
// The four majors. NOT an ATP property despite the ATP_* ids: the slams are run
// by the ITF and the national federations and award ATP ranking points, which is
// why they sit on the same calendar. So the row is "Grand Slams", never "ATP
// Grand Slams". The ids keep their existing spelling — tracked bets and
// settlement key on them and are not worth churning over a naming quibble.
export const SLAM_IDS = ['ATP_AUSTRALIAN_OPEN', 'ATP_FRENCH_OPEN', 'ATP_WIMBLEDON', 'ATP_US_OPEN']

export const VIRTUAL_LEAGUES = {
  ATP_SLAMS: {
    label: 'Grand Slams',
    group: 'Tennis',
    members: SLAM_IDS,
  },
  // Everything the ATP actually runs: Masters 1000, ATP 500/250 and the Finals.
  // Derived by subtraction rather than listed, so a tournament added to
  // config/sports.js lands here automatically instead of silently going missing.
  ATP_TOUR: {
    label: 'ATP Tour',
    group: 'Tennis',
    members: SPORT_HIERARCHY['Tennis'].leagues.filter(
      id => id !== 'ATP_CHALLENGER' && !SLAM_IDS.includes(id)
    ),
  },
}

export const isVirtualLeague = (id) => Object.prototype.hasOwnProperty.call(VIRTUAL_LEAGUES, id)

// ─── Futures ─────────────────────────────────────────────────────────────────
//
// Season-long outright markets — no fixture, so no home team, no away team and
// no start time. They are a KIND OF MARKET for an existing league, not a league
// of their own, which is why AFL_FUTURES exists only in this file: the API is
// asked for sport AFL and futuresSportId() maps the dropdown id back down
// before the request goes out.
//
// Everything downstream renders unchanged. A futures market is the same shape
// as First Tryscorer — a list of named runners, one price each, per book — so
// getOutcomeRows() already builds the table. futuresToGames() below is the only
// piece of glue.
//
// Source of truth for keys and coverage: Scrapers/AUSSIERULES/AFL_FUTURES/README.md
export const FUTURES_LEAGUES = {
  AFL_FUTURES: { label: 'AFL Futures', group: 'Aussie Rules', sportId: 'AFL' },
}

export const isFuturesLeague = (id) => Object.prototype.hasOwnProperty.call(FUTURES_LEAGUES, id)

// The real sport id to send to the API — futures is not its own sport there.
export const futuresSportId = (id) => FUTURES_LEAGUES[id]?.sportId || id

// Label -> API key. Kept HERE and not in MARKET_TYPE_FILTER_MAPPING because
// that map is rendered directly as the EV/ARB/middles filter tree, and the bots
// do not price futures — the same reason ODDS_ONLY_MARKETS exists.
//
// Mostly one key each, no period variants.
//
// *** THE O/U AND ITS MILESTONES SHARE ONE ENTRY. *** Brownlow Player Total
// Votes carries both brownlow_player_season_votes and its _milestones twin,
// exactly as src/config/markets.js pairs them for match markets:
//     "Player Disposals": ["player_disposals", "player_disposals_milestones"]
// One dropdown entry, both keys, so a user sees the lines and the N+ rungs in
// the same table rather than having to know they are separate markets.
// splitMarketPeriod() already strips a _milestones suffix when it derives the
// base key, so the pair collapses to one market in the tree.
//
// *** brownlow_medal_winner_without_daicos IS SEASONAL. *** The key names the
// excluded player and is REPLACED each season, not carried forward — next year
// the favourite is someone else. Both the label and the key change together.
//
// ─── Subject markets ─────────────────────────────────────────────────────────
// The last three below are shaped differently from the outrights above. ONE KEY
// HOLDS MANY SUBJECTS: the outcome `name` is only "Over"/"Under", and the
// player or club is on the outcome as player_name / team_name with the line as
// `point`. They need NO rendering changes — getOutcomeRows() and outcomeKey()
// in ODDS1.jsx already branch on player_name, and on team_name for an
// Over/Under outcome, so these render as "Nick Daicos — Over 43.5" and
// "Sydney Swans — Over 102.5" the same way player props do.
//
// brownlow_3_votes is one key holding every game of every round (5,400+
// outcomes), so futuresToGames() splits it into one block PER GAME and the page
// shows one round at a time - see THREE_VOTES_KEY below.
export const FUTURES_MARKET_KEYS = {
  'Brownlow Medal Winner': ['brownlow_medal_winner'],
  'Brownlow Top 3 Finish': ['brownlow_medal_top_3_finish'],
  'Brownlow Top 5 Finish': ['brownlow_medal_top_5_finish'],
  'Brownlow Top 10 Finish': ['brownlow_medal_top_10_finish'],
  'Brownlow Top 20 Finish': ['brownlow_medal_top_20_finish'],
  'Brownlow Most 3 Vote Games': ['brownlow_medal_most_3_vote_games'],
  'Brownlow Most Games Polled': ['brownlow_medal_most_games_polled'],
  'Brownlow Most Votes Last 8 Rounds': ['brownlow_medal_most_votes_last_8_rounds'],
  'Brownlow Leader After Round 6': ['brownlow_medal_leader_after_round_6'],
  'Brownlow Leader After Round 10': ['brownlow_medal_leader_after_round_10'],
  'Brownlow Leader After Round 15': ['brownlow_medal_leader_after_round_15'],
  'Brownlow Winner (Without Nick Daicos)': ['brownlow_medal_winner_without_daicos'],
  'Brownlow Player Total Votes': [
    'brownlow_player_season_votes',
    'brownlow_player_season_votes_milestones',
  ],
  'Brownlow Club Total Votes': ['brownlow_club_season_votes'],
  'Brownlow 3 Votes': ['brownlow_3_votes'],
  'Premiership Winner': ['premiership_winner'],
}

// Every futures label, in the order the dropdown should list them.
export const FUTURES_MARKETS = Object.keys(FUTURES_MARKET_KEYS)

// Where each futures label sits in the market dropdown: GROUP, then MARKET, then
// OPTION in the third column. Markets that are one question at different
// cut-offs share a market and differ by option (Top 3/5/10/20, Leader After
// Round 6/10/15), the way a match market's quarters share one entry.
// FULL_MATCH as the option means "no choice" - the dropdown commits on the
// market click and skips the third column.
//
// Order here is the order the dropdown shows. A label missing from this table
// still appears, under 'Futures', so a new market can never silently vanish.
const FUTURES_LAYOUT = [
  ['Brownlow Medal Winner',                ['Brownlow', 'Winner', 'Winner']],
  ['Brownlow Winner (Without Nick Daicos)', ['Brownlow', 'Winner', 'Without Nick Daicos']],
  ['Brownlow Top 3 Finish',                ['Brownlow', 'Top Finish', 'Top 3']],
  ['Brownlow Top 5 Finish',                ['Brownlow', 'Top Finish', 'Top 5']],
  ['Brownlow Top 10 Finish',               ['Brownlow', 'Top Finish', 'Top 10']],
  ['Brownlow Top 20 Finish',               ['Brownlow', 'Top Finish', 'Top 20']],
  ['Brownlow Leader After Round 6',        ['Brownlow', 'Leader After Round', 'Round 6']],
  ['Brownlow Leader After Round 10',       ['Brownlow', 'Leader After Round', 'Round 10']],
  ['Brownlow Leader After Round 15',       ['Brownlow', 'Leader After Round', 'Round 15']],
  ['Brownlow Most 3 Vote Games',           ['Brownlow', 'Most 3 Vote Games', null]],
  ['Brownlow Most Games Polled',           ['Brownlow', 'Most Games Polled', null]],
  ['Brownlow Most Votes Last 8 Rounds',    ['Brownlow', 'Most Votes Last 8 Rounds', null]],
  ['Brownlow Player Total Votes',          ['Brownlow', 'Player Total Votes', null]],
  ['Brownlow Club Total Votes',            ['Brownlow', 'Club Total Votes', null]],
  // Rounds are NOT options here: they are one market filtered by the page's
  // Round filter, with no refetch - see THREE_VOTES_KEY.
  ['Brownlow 3 Votes',                     ['Brownlow', '3 Votes', null]],
  ['Premiership Winner',                   ['Premiership', 'Premiership Winner', null]],
]
const FUTURES_PLACE = new Map(FUTURES_LAYOUT)
export const futuresPlace = (label) => {
  const [group, market, option] = FUTURES_PLACE.get(label) || ['Futures', label, null]
  return { group, market, option }
}

// API key -> display label. The futures endpoint returns no market name, the
// same as the odds endpoint, so the label is ours and this is the only place
// it is written down.
export const FUTURES_MARKET_LABELS = Object.fromEntries(
  Object.entries(FUTURES_MARKET_KEYS).flatMap(([label, keys]) => keys.map(key => [key, label]))
)

// Display name -> API keys, futures included.
//
// The single lookup every caller should use. MARKET_TYPE_FILTER_MAPPING alone
// returns [] for a futures label, which reads as "market with no keys" and
// fails silently — an empty fetch and an empty market tree, no error anywhere.
export const marketKeysFor = (label) =>
  FUTURES_MARKET_KEYS[label] || MARKET_TYPE_FILTER_MAPPING[label] || []

/**
 * Futures API response -> the games[] shape the odds table already renders.
 *
 * The response is the same bookmakers[] -> markets[] -> outcomes[] nesting a
 * game entry carries, so nothing is reshaped. It is only SPLIT BY MARKET: the
 * page groups rows under a fixture, and a futures market is what stands in for
 * one, so each market becomes its own synthetic game.
 */
// ─── Brownlow 3 Votes ────────────────────────────────────────────────────────
// ONE key for the whole season. Each outcome carries `round` (0-24, 0 = the
// Opening Round) and `game` ("Sydney Swans v Carlton"), so instead of one block
// for the market it becomes one block PER GAME: the two teams parsed out of
// `game` stand in for a fixture, which is what gives the header its two crests,
// and `futuresRound` is what the page's Round filter selects on.
export const THREE_VOTES_KEY = 'brownlow_3_votes'

export const roundLabel = (round) => (round === 0 ? 'Opening Round' : `Round ${round}`)

const threeVotesToGames = (bookmakers, sportId) => {
  // "round|game" -> { round, game, books: { bookName: outcomes[] } }
  const byGame = new Map()
  bookmakers.forEach(bk => bk.markets.forEach(m => (m.outcomes || []).forEach(o => {
    if (o.round == null || !o.game) return
    const id = `${o.round}|${o.game}`
    if (!byGame.has(id)) byGame.set(id, { round: o.round, game: o.game, books: {} })
    ;(byGame.get(id).books[bk.name] = byGame.get(id).books[bk.name] || []).push(o)
  })))

  return Array.from(byGame.values()).map(({ round, game, books }) => {
    const [home, away] = game.split(/\s+v\s+/)
    return {
      game: {
        home_team: home || game,
        away_team: away || null,
        // Null like every futures block: there is no start time to show.
        // Identity still holds because the page filters to ONE round before
        // grouping, and a club plays once per round.
        commence_time: null,
        // Set so the header shows the fixture and the rows sort by price, the
        // same treatment every futures block gets.
        futuresTitle: game,
        futuresRound: round,
        tournament: 'AFL Futures',
        sport: sportId,
      },
      bookmakers: Object.entries(books).map(([name, outcomes]) => {
        const src = bookmakers.find(b => b.name === name)
        return {
          name,
          last_update: src?.last_update,
          markets: [{ key: THREE_VOTES_KEY, outcomes }],
        }
      }),
    }
  })
}

export const futuresToGames = (data, sportId) => {
  // market key -> the bookmakers pricing it, carrying only that market.
  const byMarket = {}

  ;(data?.bookmakers || []).forEach(bk => {
    ;(bk.markets || []).forEach(market => {
      ;(byMarket[market.key] = byMarket[market.key] || []).push({
        name: bk.name,
        last_update: bk.last_update,
        markets: [market],
      })
    })
  })

  return Object.entries(byMarket).flatMap(([key, bookmakers]) => {
    if (key === THREE_VOTES_KEY) return threeVotesToGames(bookmakers, sportId)
    const title = FUTURES_MARKET_LABELS[key] || key
    // Clubs priced by THIS market, from the outcomes' own team_name. The band
    // in ODDS1.jsx tells a team band from a market band by matching the label
    // against the fixture's two teams - a futures market has no fixture, so it
    // has nothing to match and every crest was dropped. This is the equivalent:
    // the set of clubs the market actually prices.
    const teamNames = new Set()
    bookmakers.forEach(bk => bk.markets.forEach(m =>
      (m.outcomes || []).forEach(o => { if (o.team_name) teamNames.add(o.team_name) })
    ))
    return {
      game: {
        // The market name stands in for the home team. Not cosmetic: gameKey,
        // gameId and groupGamesByMatch all build their identity from
        // home_team + away_team + commence_time, so leaving all three null
        // would give every futures market the SAME id — one hidden row hiding
        // all of them, and one expand expanding all of them.
        home_team: title,
        away_team: null,
        commence_time: null,
        // What the row header shows in place of "Away vs Home".
        futuresTitle: title,
        tournament: 'AFL Futures',
        // *** THE IMAGE LOOKUP NEEDS THIS. *** playerImageUrl()/teamLogoUrl()
        // resolve a per-sport folder from it, so without `sport` every headshot
        // and crest on a futures market silently resolves to null and the row
        // renders with no picture. The match path stamps it in ODDS1.jsx after
        // odds-proxy returns; futures returns early there, so it is stamped
        // here instead. It is the REAL sport id ("AFL"), never the AFL_FUTURES
        // dropdown id - crests live under the league's own folder.
        sport: sportId,
        // Consumed by the player/team band in ODDS1.jsx - see teamNames above.
        futuresTeams: teamNames,
      },
      bookmakers,
    }
  })
}

// The hierarchy the DROPDOWN renders: real, except Tennis, where the main tour
// collapses to one row. Everything below derives from this, so the virtual id
// picks up market lists and group mapping without a special case each time.
export const ODDS_HIERARCHY = Object.fromEntries(
  Object.entries(SPORT_HIERARCHY).map(([group, { leagues }]) => [
    group,
    {
      // Three non-overlapping rows instead of fifteen: no tournament appears
      // under two of them, so no two rows can return the same fixtures.
      //
      // Futures rows sit alongside the real leagues of their group (AFL Futures
      // under Aussie Rules). They are appended rather than listed in
      // SPORT_HIERARCHY because that map is shared with every other page, and
      // only this one has anywhere to show a market with no fixture.
      leagues: group === 'Tennis'
        ? ['ATP_SLAMS', 'ATP_TOUR', 'ATP_CHALLENGER']
        : [...leagues, ...Object.keys(FUTURES_LEAGUES).filter(id => FUTURES_LEAGUES[id].group === group)],
    },
  ])
)

// Label for a league row — virtual leagues carry their own, the rest use the
// shared display names.
export const leagueLabel = (id) =>
  VIRTUAL_LEAGUES[id]?.label || FUTURES_LEAGUES[id]?.label || SPORT_DISPLAY_NAMES[id] || id

// Markets the ODDS SCREEN carries that no other page does. buildMarketTree
// folds these into the group named here, exactly as if they sat in
// MARKET_TYPE_HIERARCHY — they are kept out of it because the EV page renders
// that map directly as its market filter, and the bots do not price these.
//
// The winning-margin bands are one key PER CUT (AFL prices the 39 and 24 cuts,
// NRL the 12) and must never merge: they are different bets on the same game.
// Which sports actually offer them is decided by SPORT_GROUP_MARKETS below.
export const ODDS_ONLY_MARKETS = {
  'Game Markets': [
    'Winning Margin 1-39 / 40+',
    'Winning Margin 1-24 / 25+',
    'Winning Margin 1-12 / 13+',
    // MMA. The bots price only H2H / Goes The Distance / Total Rounds, so the
    // rest of the UFC markets live here rather than in MARKET_TYPE_HIERARCHY.
    'How Fight Ends',
    'Method of Victory',
    'Round Betting',
    'Fight To Start Round',
  ],
  // MMA fighter props: Over-only N+ ladders with no O/U twin, so no bot can
  // price them - odds screen only, for the same reason as the markets above.
  'Player Props': [
    'Fighter Significant Strikes',
    'Fighter Total Strikes',
    'Fighter Takedowns',
  ],
  // Futures groups (Brownlow / Premiership), so futures
  // never mixes into a match market category. They exist ONLY here -
  // buildMarketTree unions these keys with MARKET_TYPE_HIERARCHY's for that
  // reason. Built from futuresPlace() so the layout table is the one source.
  // Layout order first, then any label the table does not know yet.
  ...[...FUTURES_LAYOUT.map(([l]) => l).filter(l => FUTURES_MARKET_KEYS[l]),
      ...FUTURES_MARKETS.filter(l => !FUTURES_PLACE.has(l))].reduce((acc, label) => {
    const { group } = futuresPlace(label)
    ;(acc[group] = acc[group] || []).push(label)
    return acc
  }, {}),
}

// A futures league carries ONLY its own markets — the group's match markets
// (Head To Head, Player Disposals) have no meaning without a fixture.
export const SPORT_AVAILABLE_MARKETS = Object.fromEntries(
  Object.entries(ODDS_HIERARCHY).flatMap(([group, { leagues }]) =>
    leagues.map(league => [
      league,
      isFuturesLeague(league) ? FUTURES_MARKETS : SPORT_GROUP_MARKETS[group] || [],
    ])
  )
)

// Map league -> sport group (e.g. NBA -> 'Basketball'), derived from SPORT_HIERARCHY.
// Used below to scope Player Props sub-categories to the selected sport's group only —
// otherwise NBA shows "Hockey: Player Points" too, since shared codes appear in both.
export const LEAGUE_TO_GROUP = Object.fromEntries(
  Object.entries(ODDS_HIERARCHY).flatMap(([group, { leagues }]) =>
    leagues.map(league => [league, group])
  )
)

// ─── Market tree: group → market → period ────────────────────────────────────
//
// The dropdown used to list every period variant as its own row, so soccer ran
// to 55 entries and half of them differed only by "- 1st Half". Period is really
// a third axis, so it gets its own column and the market list collapses to the
// distinct markets underneath (soccer 55 -> 27).
//
// *** THE PERIOD COMES FROM THE RAW API KEY, NEVER THE DISPLAY LABEL. ***
// Keys are systematic (head_to_head_1st_half, alternate_lines_games_1st_set),
// labels are not: the period sits at the end of "Head To Head - 1st Half" but
// mid-string in "Match Handicap - 1st Half (Asian)", and "First Goalscorer" /
// "First Tryscorer" contain a period word while being nothing of the kind.
// Parsing labels mangles those three cases; parsing keys handles all of them.
//
// Longest suffix first, so _1st_5_innings is not shadowed by a shorter match.
export const PERIOD_SUFFIXES = [
  // Baseball prices a single first inning as well as the first five. Its key is
  // _1st_1_innings — a COUNT, not an ordinal — so it matched nothing here and
  // "Total Runs - 1st Inning" sat in the Market column as its own market
  // instead of collapsing into Total Runs' periods.
  ['_1st_1_innings', '1st Inning'],
  ['_1st_5_innings', '1st 5 Innings'],
  ['_1st_quarter', '1st Quarter'], ['_2nd_quarter', '2nd Quarter'],
  ['_3rd_quarter', '3rd Quarter'], ['_4th_quarter', '4th Quarter'],
  ['_1st_period', '1st Period'], ['_2nd_period', '2nd Period'], ['_3rd_period', '3rd Period'],
  ['_1st_half', '1st Half'], ['_2nd_half', '2nd Half'],
  ['_1st_set', '1st Set'], ['_2nd_set', '2nd Set'],
]
export const FULL_MATCH = 'Full Match'

export const splitMarketPeriod = (label) => {
  const key = (marketKeysFor(label))[0] || ''
  const bare = key.replace(/_milestones$/, '')
  for (const [suffix, period] of PERIOD_SUFFIXES) {
    if (bare.endsWith(suffix)) return { baseKey: bare.slice(0, -suffix.length), period }
  }
  return { baseKey: bare, period: FULL_MATCH }
}

// { group: { baseKey: { label, periods: { periodName: marketLabel } } } }
export const buildMarketTree = (availableLabels) => {
  const allowed = new Set(availableLabels)
  const tree = {}
  // The UNION of both maps' groups. Iterating MARKET_TYPE_HIERARCHY alone would
  // skip any group that exists only in ODDS_ONLY_MARKETS (today: Futures), and
  // its markets would silently never reach the dropdown.
  const groups = [...new Set([
    ...Object.keys(MARKET_TYPE_HIERARCHY),
    ...Object.keys(ODDS_ONLY_MARKETS),
  ])]
  const hierarchy = groups.map(group => (
    [group, [
      ...Object.values(MARKET_TYPE_HIERARCHY[group] || {}).flat(),
      ...(ODDS_ONLY_MARKETS[group] || []),
    ]]
  ))
  hierarchy.forEach(([group, groupMarkets]) => {
    [groupMarkets].forEach(markets => {
      markets.forEach(label => {
        if (!allowed.has(label)) return
        if (FUTURES_MARKET_KEYS[label]) {
          // Futures: base and option come from the layout table. The display
          // name is set here, because the fallback below would name the market
          // after its full label ("Brownlow Top 3 Finish") instead of the base.
          const { market, option } = futuresPlace(label)
          const baseKey = `futures:${market}`
          tree[group] = tree[group] || {}
          tree[group][baseKey] = tree[group][baseKey] || { periods: {}, futuresLabel: market }
          tree[group][baseKey].periods[option ?? FULL_MATCH] = label
          return
        }
        const { baseKey, period } = splitMarketPeriod(label)
        tree[group] = tree[group] || {}
        tree[group][baseKey] = tree[group][baseKey] || { periods: {} }
        tree[group][baseKey].periods[period] = label
      })
    })
  })
  // Name each market by its full-match label where one exists. Where it does not
  // (a market only ever priced per period), fall back to the shortest variant
  // with the period words stripped out.
  Object.values(tree).forEach(group =>
    Object.values(group).forEach(entry => {
      entry.label = entry.futuresLabel || entry.periods[FULL_MATCH] || Object.values(entry.periods)
        .map(l => l.replace(/\s*-\s*(1st|2nd|3rd|4th)[^(]*/, ' ').replace(/\s+/g, ' ').trim())
        .sort((a, b) => a.length - b.length)[0]
    })
  )
  return tree
}

// Periods in calendar order rather than however they were encountered.
export const PERIOD_ORDER = [FULL_MATCH, ...PERIOD_SUFFIXES.map(([, name]) => name)]
// Anything NOT a known period (a futures option: "Top 5", "Without Nick
// Daicos") follows in the order it was added, which is the layout table's order.
// Without this the filter below dropped every futures option and the third
// column came up empty.
export const orderPeriods = (periods) => [
  ...PERIOD_ORDER.filter(p => Object.prototype.hasOwnProperty.call(periods, p)),
  ...Object.keys(periods).filter(p => !PERIOD_ORDER.includes(p)),
]

/**
 * Sport groups where the "players" ARE the two competitors.
 *
 * Tennis reuses home_team/away_team to hold PLAYER names (see TENNIS_MARKETS in
 * Bots/shared_odds_service.py — the schema field names are deliberately reused
 * so tennis is a drop-in downstream). So a tennis player prop's `player_name`
 * is one of the two people the Games list already names, and a Players filter
 * restates the Games filter with twice the rows and worse labels.
 *
 * A group listed here shows Games only. Add WTA, ITF, boxing or darts here if
 * they ever land — anything where a "game" is two named people.
 *
 * Deliberately an explicit list rather than inferring it by comparing player
 * names against team names: the two can be formatted differently by the feed
 * ("N. Djokovic" vs "Novak Djokovic"), and a heuristic that silently stops
 * matching would put the redundant filter back with nobody noticing.
 */
export const PLAYER_VS_PLAYER_GROUPS = new Set(['Tennis', 'MMA'])

export const playersAreCompetitors = (sportId) =>
  PLAYER_VS_PLAYER_GROUPS.has(LEAGUE_TO_GROUP[sportId])
